import {
  useEffect,
  useState
} from "react";

import {
  useParams,
  useNavigate
} from "react-router-dom";

import {
  getItemLedger
} from "../../services/itemLedgerService";

import {
  getLedgerLookups
} from "../../services/inventoryLedgerService";

import {
  ledgerEffectText
} from "../../utils/itemLedgerBalance";

import {
  ledgerShortDate,
  withReferenceNo
} from "../../utils/ledgerView";

const TH =
  "sticky top-0 z-10 bg-slate-900 text-white text-left font-semibold px-2 py-2";

const CELL =
  "px-2 py-1.5 border-b align-top";

export default function ItemLedger() {

  const { itemId } =
    useParams();

  const navigate =
    useNavigate();

  const [
    loading,
    setLoading
  ] = useState(true);

  const [
    ledger,
    setLedger
  ] = useState([]);

  // =========================================
  // ROUTE PARAM
  // the item's item_id (P4-5 — was item_code, which items shared)
  // =========================================

  const decodedItemId =
    decodeURIComponent(
      itemId || ""
    );

  /* =================================================
     FETCH LEDGER
  ================================================= */

  const fetchLedger =
    async () => {

      try {

        setLoading(true);

        // =====================================
        // TEMP SAFE TENANT SOURCE
        // =====================================

        const user = JSON.parse(localStorage.getItem("user") || "{}");
const companyId = user?.company_id || "";

        // =====================================
        // SST SAFE FETCH
        // =====================================

        // Same PO number lookup as Inventory Ledger: a failed read
        // gives an empty list, and the row keeps its doc id.
        const [data, lookups] =
          await Promise.all([
            getItemLedger(
              companyId,
              decodedItemId
            ),
            getLedgerLookups(
              companyId
            )
          ]);

        console.log(
          "ITEM LEDGER:",
          data
        );

        setLedger(
          withReferenceNo(
            Array.isArray(data)
              ? data
              : [],
            lookups.poNumbers
          )
        );

      } catch (error) {

        console.error(
          "ITEM LEDGER PAGE ERROR",
          error
        );

        alert(
          "Failed to load item ledger"
        );

      } finally {

        setLoading(false);
      }
    };

  /* =================================================
     INITIAL LOAD
  ================================================= */

  useEffect(() => {
    if (decodedItemId) {
      fetchLedger();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decodedItemId]);

  /* =================================================
     HELPERS
  ================================================= */

  const getEffectColor =
    (
      value
    ) => {

      if (value > 0) {

        return "text-green-600";
      }

      if (value < 0) {

        return "text-red-600";
      }

      return "text-gray-600";
    };

  const getMovementBadge =
    (
      movement
    ) => {

      if (
        movement?.includes(
          "GRN"
        )
      ) {

        return "bg-green-100 text-green-700";
      }

      if (
        movement?.includes(
          "ISSUE"
        )
      ) {

        return "bg-red-100 text-red-700";
      }

      if (
        movement?.includes(
          "RETURN"
        )
      ) {

        return "bg-blue-100 text-blue-700";
      }

      if (
        movement?.includes(
          "TRANSFER"
        )
      ) {

        return "bg-yellow-100 text-yellow-700";
      }

      return "bg-slate-100 text-slate-700";
    };

  /* =================================================
     LOADING
  ================================================= */

  if (loading) {

    return (

      <div className="
        p-6
        text-lg
        font-medium
      ">

        Loading Item Ledger...

      </div>
    );
  }

  /* =================================================
     HEADER DATA
  ================================================= */

  const itemName =
    ledger?.[0]?.item_name ||
    "Item";

  // The item id, not the newest row's item_code: rows keep the code they
  // were written with, so that one may be an old code (P4-5).
  const itemIdLabel =
    decodedItemId ||
    "-";

  /* =================================================
     UI
  ================================================= */

  return (

    <div className="
      min-h-screen
      bg-slate-100
      p-2
    ">

      <div className="
        w-full
        bg-white
        rounded-2xl
        shadow
        border
        p-4
      ">

        {/* ===================================== */}
        {/* HEADER */}
        {/* ===================================== */}

        <div className="
          flex
          items-center
          justify-between
          mb-3
        ">

          <div>

            <h1 className="
              text-2xl
              font-bold
              text-slate-800
            ">

              {itemName}

            </h1>

            <div className="
              text-gray-500
              mt-1
            ">

              {itemIdLabel}

            </div>

          </div>

          <button

            onClick={() =>

              navigate(
                "/inventory-ledger"
              )
            }

            className="
              px-4
              py-2
              rounded-lg
              bg-slate-800
              text-white
              hover:bg-slate-700
            "
          >

            Back

          </button>

        </div>

        {/* ===================================== */}
        {/* ENTRY COUNT */}
        {/* ===================================== */}

        <div className="
          mb-3
          text-sm
          text-gray-500
        ">

          Total Entries:
          {" "}
          {ledger.length}

        </div>

        {/* ===================================== */}
        {/* TABLE — its own scroll box, so the header stays in view */}
        {/* ===================================== */}

        <div className="
          overflow-auto
          rounded-xl
          border
          max-h-[calc(100vh-13rem)]
        ">

          <table className="
            w-full
            min-w-[760px]
            table-fixed
            border-separate
            border-spacing-0
            text-sm
          ">

            <thead>

              <tr>

                <th className={`${TH} w-[92px]`}>
                  Date
                </th>

                <th className={`${TH} w-[180px]`}>
                  Movement
                </th>

                <th className={`${TH} w-[90px]`}>
                  Qty
                </th>

                <th className={`${TH} w-[70px]`}>
                  Effect
                </th>

                <th className={`${TH} w-[80px]`}>
                  Balance
                </th>

                <th className={`${TH} w-[220px]`}>
                  Site / Project
                </th>

                <th className={TH}>
                  Reference
                </th>

              </tr>

            </thead>

            <tbody>

              {ledger.length === 0 ? (

                <tr>

                  <td
                    colSpan="7"
                    className="
                      p-6
                      text-center
                      text-gray-500
                    "
                  >

                    No ledger entries found

                  </td>

                </tr>

              ) : (

                ledger.map(

                  (
                    entry,
                    index
                  ) => (

                    <tr

                      key={
                        entry.id ||
                        `${entry.reference_id}-${index}`
                      }

                      className={`
                        transition-all
                        hover:bg-slate-100
                        ${
                          index % 2 === 0
                            ? "bg-slate-50"
                            : "bg-white"
                        }
                      `}
                    >

                      {/* DATE */}

                      <td className={`${CELL} text-xs text-gray-600`}>

                        {ledgerShortDate(
                          entry.created_at
                        )}

                      </td>

                      {/* MOVEMENT */}

                      <td className={CELL}>

                        <span
                          className={`
                            inline-block
                            max-w-full
                            break-all
                            px-2
                            py-0.5
                            rounded-lg
                            text-[11px]
                            font-semibold
                            ${getMovementBadge(
                              entry.movement_type
                            )}
                          `}
                        >

                          {entry.movement_type}

                        </span>

                        {entry.ledger_kind === "site" && (

                          <span className="
                            ml-1
                            px-2
                            py-0.5
                            rounded-full
                            text-[11px]
                            font-semibold
                            bg-slate-200
                            text-slate-600
                          ">

                            Site

                          </span>
                        )}

                      </td>

                      {/* QTY + UNIT */}

                      <td className={`${CELL} font-medium`}>

                        {entry.qty}
                        {" "}
                        <span className="
                          text-xs
                          font-normal
                          text-gray-500
                        ">

                          {entry.unit || ""}

                        </span>

                      </td>

                      {/* EFFECT */}

                      <td
                        className={`
                          ${CELL}
                          font-bold
                          ${entry.ledger_kind === "store"
                            ? getEffectColor(
                                entry.stock_effect
                              )
                            : "text-gray-400"}
                        `}
                      >

                        {ledgerEffectText(entry)}

                      </td>

                      {/* STORE BALANCE (P4-5c — site rows never count) */}

                      <td className={`${CELL} font-semibold`}>

                        {entry.store_balance}

                      </td>

                      {/* SITE / PROJECT */}

                      <td className={CELL}>

                        <div
                          className="truncate"
                          title={entry.site_name || undefined}
                        >

                          {entry.site_name || "-"}

                        </div>

                        <div
                          className="
                            text-xs
                            text-gray-500
                            truncate
                          "
                          title={entry.project_name || undefined}
                        >

                          {entry.project_name || "-"}

                        </div>

                      </td>

                      {/* REFERENCE — remarks in full on hover */}

                      <td
                        className={CELL}
                        title={entry.remarks || undefined}
                      >

                        <div className="
                          text-xs
                          font-medium
                          truncate
                        ">

                          {entry.reference_type || "-"}

                        </div>

                        <div className="
                          text-xs
                          text-gray-600
                          truncate
                        ">

                          {entry.reference_no || "-"}

                        </div>

                        {entry.remarks && (

                          <div className="
                            text-xs
                            text-gray-400
                            truncate
                          ">

                            {entry.remarks}

                          </div>
                        )}

                      </td>

                    </tr>
                  )
                )
              )}

            </tbody>

          </table>

        </div>

      </div>

    </div>
  );
}